<template>
  <el-dialog
    title="编辑"
    :visible.sync="visible"
    width="800px"
    :before-close="handleClose"
    append-to-body
    custom-class="tag-selection-dialog"
    v-loading="loading"
  >
    <!-- Search Section -->
    <div class="search-section">
      <div class="search-item-left">
        <el-select
          v-model="selectedWechatSearch"
          placeholder="微信标签"
          clearable
          filterable
          style="width: 200px; margin-right: 10px;"
        >
          <el-option
            v-for="tag in wechatTagOptions"
            :key="tag.wechatTagId"
            :label="`(${tag.wechatTagId}) ${tag.wechatTagName}`"
            :value="tag.wechatTagId"
          />
        </el-select>
        <el-select
          v-model="selectedLeboSearch"
          placeholder="乐播标签"
          clearable
          filterable
          style="width: 200px; margin-right: 10px;"
        >
          <el-option
            v-for="tag in leboTagOptionsForSearch"
            :key="tag.leboTagId"
            :label="`(${tag.leboTagId}) ${tag.leboTagName}`"
            :value="tag.leboTagId"
          />
        </el-select>
      </div>
      <el-button class="search-item-right" type="primary" @click="handleSearch">搜索</el-button>
    </div>

    <!-- Tag Table -->
    <div class="tag-table-container">
      <el-table 
        :data="filteredTagList" 
        border
        style="width: 100%"
        height="500"
        :empty-text="'暂无标签'"
      >
        <el-table-column width="60" align="center">
          <template #header>
            <el-checkbox
              :indeterminate="isIndeterminate"
              :value="isCheckAll"
              @change="handleCheckAllChange"
            />
          </template>
          <template slot-scope="scope">
            <el-checkbox
              :value="isTagSelected(scope.row.wechatTagId)"
              @change="handleTagChange(scope.row.wechatTagId, $event)"
            />
          </template>
        </el-table-column>
        <el-table-column prop="wechatTagName" label="微信标签" min-width="200">
          <template slot-scope="scope">
            {{ scope.row.wechatTagName }}
          </template>
        </el-table-column>
        <el-table-column prop="leboTagName" label="乐播标签" min-width="200">
          <template slot-scope="scope">
            {{ scope.row.leboTagName || '-' }}
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Selected Count -->
    <div class="selected-count">
      已选择 ( <span style="color: #409EFF;">{{ selectedWechatTags.length }}</span> )
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取 消</el-button>
      <el-button type="primary" @click="handleSubmit">保 存</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getAllTagsList } from '@/api/operationsManage/weChatUserManage'

export default {
  name: 'TagSelectionDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    wechatTags: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      loading: false,
      tagListData: [],
      selectedWechatSearch: null,
      selectedLeboSearch: null,
      selectedWechatTags: [],
      filteredTagList: []
    }
  },
  computed: {
    // 提取所有唯一的微信标签选项（用于搜索下拉框）
    wechatTagOptions() {
      const wechatTagsMap = new Map()
      this.tagListData.forEach(tag => {
        if (tag.wechatTagId && tag.wechatTagName) {
          if (!wechatTagsMap.has(tag.wechatTagId)) {
            wechatTagsMap.set(tag.wechatTagId, {
              wechatTagId: tag.wechatTagId,
              wechatTagName: tag.wechatTagName
            })
          }
        }
      })
      return Array.from(wechatTagsMap.values())
    },
    // 提取所有唯一的乐播标签选项（用于搜索下拉框）
    leboTagOptionsForSearch() {
      const leboTagsMap = new Map()
      this.tagListData.forEach(tag => {
        if (tag.leboTagId && tag.leboTagName) {
          if (!leboTagsMap.has(tag.leboTagId)) {
            leboTagsMap.set(tag.leboTagId, {
              leboTagId: tag.leboTagId,
              leboTagName: tag.leboTagName
            })
          }
        }
      })
      return Array.from(leboTagsMap.values())
    },
    // 是否全选
    isCheckAll() {
      if (this.filteredTagList.length === 0) return false
      return this.filteredTagList.every(item => this.selectedWechatTags.includes(item.wechatTagId))
    },
    // 是否半选状态
    isIndeterminate() {
      const checkedCount = this.filteredTagList.filter(item => 
        this.selectedWechatTags.includes(item.wechatTagId)
      ).length
      return checkedCount > 0 && checkedCount < this.filteredTagList.length
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.fetchTagList()
        this.selectedWechatTags = [...this.wechatTags]
        this.selectedWechatSearch = null
        this.selectedLeboSearch = null
      }
    }
  },
  methods: {
    async fetchTagList() {
      this.loading = true
      try {
        const res = await getAllTagsList()
        const records = res.data.data || []
        // 映射后端字段到前端使用的字段
        this.tagListData = records.map(item => ({
          wechatTagId: item.vxTagId || item.wechatTagId,
          wechatTagName: item.vxTagName || item.wechatTagName,
          leboTagId: item.leboTagId,
          leboTagName: item.leboTagName
        }))
        this.handleSearch()
      } catch (error) {
        console.error('获取标签列表失败:', error)
        this.$message.error('获取标签列表失败')
        this.tagListData = []
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      // 如果两个条件都没有选择，显示全部
      if (!this.selectedWechatSearch && !this.selectedLeboSearch) {
        this.filteredTagList = [...this.tagListData]
        return
      }
      
      // 如果只选择了微信标签
      if (this.selectedWechatSearch && !this.selectedLeboSearch) {
        this.filteredTagList = this.tagListData.filter(item => item.wechatTagId === this.selectedWechatSearch)
        return
      }
      
      // 如果只选择了乐播标签
      if (this.selectedLeboSearch && !this.selectedWechatSearch) {
        this.filteredTagList = this.tagListData.filter(item => item.leboTagId === this.selectedLeboSearch)
        return
      }
      
      // 如果两个条件都选择了，取并集（匹配微信标签的项 + 匹配乐播标签的项，去重）
      if (this.selectedWechatSearch && this.selectedLeboSearch) {
        const wechatFiltered = this.tagListData.filter(item => item.wechatTagId === this.selectedWechatSearch)
        const leboFiltered = this.tagListData.filter(item => item.leboTagId === this.selectedLeboSearch)
        // 合并并去重
        const combined = [...wechatFiltered, ...leboFiltered]
        this.filteredTagList = combined.filter((item, index, self) =>
          index === self.findIndex(t => t.wechatTagId === item.wechatTagId && t.leboTagId === item.leboTagId)
        )
      }
    },
    handleCheckAllChange(value) {
      if (value) {
        // 全选
        this.filteredTagList.forEach(item => {
          if (!this.selectedWechatTags.includes(item.wechatTagId)) {
            this.selectedWechatTags.push(item.wechatTagId)
          }
        })
      } else {
        // 取消全选
        this.filteredTagList.forEach(item => {
          const index = this.selectedWechatTags.indexOf(item.wechatTagId)
          if (index > -1) {
            this.selectedWechatTags.splice(index, 1)
          }
        })
      }
    },
    handleTagChange(tagId, checked) {
      if (checked) {
        if (!this.selectedWechatTags.includes(tagId)) {
          this.selectedWechatTags.push(tagId)
        }
      } else {
        const index = this.selectedWechatTags.indexOf(tagId)
        if (index > -1) {
          this.selectedWechatTags.splice(index, 1)
        }
      }
    },
    isTagSelected(tagId) {
      return this.selectedWechatTags.includes(tagId)
    },
    handleClose() {
      this.$emit('update:visible', false)
    },
    handleSubmit() {
      // 如果选择的微信标签数量大于20, 则无法提交
      if (this.selectedWechatTags.length > 20) {
        this.$message.warning('选择的微信标签数量最多为20个')
        return
      }

      const submitItems = this.tagListData.filter(item => this.selectedWechatTags.includes(item.wechatTagId))
      this.$emit('submit', {
        wechatTags: this.selectedWechatTags,
        submitItems: submitItems
      })
      this.handleClose()
    }
  }
}
</script>

<style lang="scss" scoped>
.search-section {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  justify-content: space-between;
}

.search-item-left {
  display: flex;
  align-items: center;
}

.search-item-right {
  margin-left: 10px;
}
.tag-table-container {
  margin-bottom: 15px;
}

.selected-count {
  color: #606266;
  font-size: 14px;
  margin-bottom: 10px;
}
</style>
